<script setup lang="ts">
  import type { IHostAdapter } from '@/gpthelp/cartadigital-admin-host-tools';
  import {
    createAdminHostAdapter,
    warmAdminHostContext,
  } from '@/gpthelp/gpthelp-host-adapter';
  import {
    ensureGpthelpDefined,
    gpthelpChatbotId,
  } from '@/gpthelp/gpthelp-loader';
  import { isGpthelpSupportedBrowser } from '@/gpthelp/gpthelp-support';
  import { useGpthelpScope } from '@/composition/assistant/use-gpthelp-scope';
  import {
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useRouter } from 'vue-router/composables';

  /**
   * The legacy GPTHelp bubble, on every page without the «Asistente» panel
   * (V2AssistantRoot), before and after GA (plan §9.14): logged-out pages and
   * pages outside a store.
   */
  interface IGptHelpElement extends HTMLElement {
    hostAdapter?: IHostAdapter;
  }

  const props = withDefaults(
    defineProps<{
      enabled?: boolean;
      id?: string;
      mode?: 'bubble' | string;
    }>(),
    {
      enabled: true,
      id: undefined,
      mode: 'bubble',
    }
  );

  const supported = isGpthelpSupportedBrowser();
  const chatbotId = props.id ?? gpthelpChatbotId();

  const vm = getCurrentInstance()!.proxy;
  const router = useRouter();
  const { scopeKey, scopeLabel } = useGpthelpScope();

  /**
   * The widget is rendered from App.vue before the route guard has awaited the
   * restaurant fetch. Mounting only after the initial navigation resolved means
   * restaurant pages create the element with its scope-key already present,
   * instead of loading the unscoped conversation first and switching right after.
   */
  const routeReady = ref(false);
  router.onReady(
    () => {
      routeReady.value = true;
    },
    () => {
      routeReady.value = true;
    }
  );

  // Warm the business-day cache right after a restaurant/branch switch.
  watch(
    scopeKey,
    () => {
      if (!props.enabled || !supported) return;
      warmAdminHostContext();
    },
    { immediate: true }
  );

  const hostAdapter = createAdminHostAdapter();

  const gptHelpEl = ref<IGptHelpElement | null>(null);
  const didAttach = ref(false);
  let didUnmount = false;
  let inflight: Promise<void> | null = null;

  onBeforeUnmount(() => {
    didUnmount = true;
  });

  const attachHostAdapter = (el: IGptHelpElement) => {
    const ownDescriptor = Object.getOwnPropertyDescriptor(el, 'hostAdapter');
    const hasOwnDataProp = !!ownDescriptor && 'value' in ownDescriptor;

    // If the element already has a hostAdapter via the custom element's accessor,
    // don't override it.
    if (!hasOwnDataProp && el.hostAdapter) {
      didAttach.value = true;
      return;
    }

    // If a data-property was assigned before upgrade, it can shadow the accessor.
    // Only delete in that case (never delete the accessor itself).
    if (hasOwnDataProp) {
      try {
        delete el.hostAdapter;
      } catch {
        // Ignore: if it's non-configurable, we can't fix it here.
      }
    }

    el.hostAdapter = hostAdapter;
    didAttach.value = true;
  };

  const ensureReadyAndAttach = async () => {
    if (!props.enabled) return;
    if (!supported) return;
    if (didUnmount) return;
    if (didAttach.value) return;
    if (!gptHelpEl.value) return;

    if (inflight) return await inflight;

    inflight = (async () => {
      await ensureGpthelpDefined();
      if (didUnmount || didAttach.value) return;
      if (!gptHelpEl.value) return;
      attachHostAdapter(gptHelpEl.value);
    })().finally(() => {
      inflight = null;
    });

    await inflight;
  };

  onMounted(() => {
    if (!props.enabled || !supported) return;
    // Start downloading the widget bundle right away; the element itself is
    // created later (routeReady) and attached by the ref watcher below.
    ensureGpthelpDefined().catch((error) => {
      console.warn('[gpthelp] Failed to load widget', error);
    });
  });

  // The bubble speaks the admin language, like the panel (plan §9.2): the
  // element reads its new `locale` attribute on the next tick, then re-reads
  // the welcome and the ⋯ items the adapter builds in that language.
  watch(
    () => vm.$i18n.locale,
    () =>
      void nextTick(() =>
        gptHelpEl.value?.dispatchEvent(
          new CustomEvent('gpthelp:context-changed')
        )
      )
  );

  // The <gpt-help> element is created/destroyed by the root v-if (enabled,
  // routeReady). Attach the adapter whenever a fresh element appears.
  watch(gptHelpEl, (el) => {
    if (!el) {
      didAttach.value = false;
      return;
    }
    ensureReadyAndAttach().catch((error) => {
      console.warn('[gpthelp] Failed to load/attach widget', error);
    });
  });
</script>

<template>
  <gpt-help
    v-if="enabled && supported && routeReady"
    ref="gptHelpEl"
    class="gpt-help-widget"
    :id="chatbotId"
    :mode="mode"
    :scope-key="scopeKey"
    :scope-label="scopeLabel"
    :locale="$i18n.locale"
  />
</template>

<style lang="scss">
  // An open bottom sheet hides the bubble (`useHideHelpBubble`).
  body.gpthelp-bubble-hidden gpt-help.gpt-help-widget {
    visibility: hidden !important;
  }
</style>
